// src/components/System/Permission.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  getPermissions,
  getPermissionCategories,
  getPermissionStats,
  createPermission,
  updatePermission,
  deletePermission,
  getPermissionsByCategory,
  getUserPermissions,
  getRolePermissions,
  syncUserPermissions,
  syncUserPermissionsBySlugs,
  syncRolePermissions,
  syncRolePermissionsBySlugs,
  clearApiMessage,
  clearError,
  clearPermissionState,
  selectPermissions,
  selectPermissionPagination,
  selectPermissionCategories,
  selectCategoryDisplayNames,
  selectPermissionStats,
  selectPermissionsLoading,
  selectPermissionsApiMessage,
  selectPermissionsError,
} from '../../store/slices/permissionSlice';
import { selectApiMessage, selectAuthError } from '../../store/slices/authSlice';
import { Plus, Pencil, Trash2, AlertTriangle, ClipboardList, CheckCircle2, XCircle, Key, Folder, Star, Settings, Eye, Lock, User, Users, Hourglass, Search, List, LayoutGrid } from 'lucide-react';

const Permission = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [viewMode, setViewMode] = useState('table');
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [editingPermissionId, setEditingPermissionId] = useState(null);
  const [activeTab, setActiveTab] = useState('permissions');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [userPermissions, setUserPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Track if toast has been shown to prevent duplicates
  const toastShownRef = useRef(false);

  // ==================== REFS ====================
  const labelRef = useRef(null);
  const categoryRef = useRef(null);
  const descriptionRef = useRef(null);
  const levelRef = useRef(null);
  const isSystemRef = useRef(null);

  // ==================== SELECTORS ====================
  const permissions = useSelector(selectPermissions) || [];
  const pagination = useSelector(selectPermissionPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const categories = useSelector(selectPermissionCategories) || [];
  const categoryDisplayNames = useSelector(selectCategoryDisplayNames) || {};
  const stats = useSelector(selectPermissionStats) || {
    total: 0,
    by_category: {},
    by_level: {},
    system_count: 0
  };
  const loading = useSelector(selectPermissionsLoading);
  const apiMessage = useSelector(selectPermissionsApiMessage);
  const error = useSelector(selectPermissionsError);
  const authError = useSelector(selectAuthError);

  // ==================== HELPERS ====================
  const getCategoryDisplayName = useCallback((categoryKey) => {
    if (!categoryKey) return 'Unknown';
    return categoryDisplayNames[categoryKey] || categoryKey;
  }, [categoryDisplayNames]);

  // ==================== EFFECTS ====================

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await Promise.all([
          dispatch(getPermissions({ per_page: itemsPerPage, page: currentPage })),
          dispatch(getPermissionCategories()),
          dispatch(getPermissionStats())
        ]);
        
        const result = await dispatch(getPermissions({ per_page: 1000 }));
        if (result.payload?.success) {
          setAllPermissions(result.payload.data?.data || []);
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error('Error loading initial data:', error);
        setIsInitialized(true);
      }
    };
    
    loadInitialData();
    
    return () => {
      dispatch(clearPermissionState());
    };
  }, []); // Only runs once on mount

  // Handle search and filters
  useEffect(() => {
    if (!isInitialized) return;
    
    const timer = setTimeout(() => {
      const params = {
        search: searchTerm,
        per_page: itemsPerPage,
        page: currentPage
      };
      if (selectedCategory) params.category = selectedCategory;
      if (selectedLevel) params.level = selectedLevel;
      
      dispatch(getPermissions(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, selectedCategory, selectedLevel, currentPage, itemsPerPage, dispatch, isInitialized]);

  // Display API messages
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error || !!authError)) return;
    
    if (toastShownRef.current) {
      setTimeout(() => {
        toastShownRef.current = false;
      }, 1000);
      return;
    }
    
    toastShownRef.current = true;
    
    const isError = error || authError;
    const toastType = isError ? 'error' : 'success';
    
    showToast(apiMessage, toastType);
    
    const clearTimer = setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
      toastShownRef.current = false;
    }, 3000);

    return () => {
      clearTimeout(clearTimer);
    };
  }, [apiMessage, error, authError, dispatch, showToast]);

  // Reset toast ref on unmount
  useEffect(() => {
    return () => {
      toastShownRef.current = false;
    };
  }, []);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== PAGINATION ====================
  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(pagination.last_page || 1, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== CRUD OPERATIONS ====================

  const handleCreatePermission = () => {
    setTimeout(() => {
      if (labelRef.current) labelRef.current.value = '';
      if (categoryRef.current) categoryRef.current.value = '';
      if (descriptionRef.current) descriptionRef.current.value = '';
      if (levelRef.current) levelRef.current.value = '1';
      if (isSystemRef.current) isSystemRef.current.checked = false;
    }, 50);

    toastShownRef.current = false;

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Plus size={16} style={{ verticalAlign: '-3px' }} /> Create New Permission</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Label <span style={{ color: 'var(--red)' }}>*</span></label>
            <input
              className="form-input"
              ref={labelRef}
              placeholder="e.g. View Members"
            />
            <div className="form-hint">Slug will be auto-generated from the label</div>
          </div>
          <div className="form-group">
            <label className="form-label">Category <span style={{ color: 'var(--red)' }}>*</span></label>
            <select className="form-select" ref={categoryRef} defaultValue="">
              <option value="">Select Category</option>
              {Array.isArray(categories) && categories.length > 0 ? (
                categories.map(catKey => (
                  <option key={catKey} value={catKey}>
                    {getCategoryDisplayName(catKey)}
                  </option>
                ))
              ) : (
                <option value="" disabled>No categories available</option>
              )}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              rows="2"
              ref={descriptionRef}
              placeholder="Brief description of this permission"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Level <span style={{ color: 'var(--red)' }}>*</span></label>
            <select className="form-select" ref={levelRef} defaultValue="1">
              <option value="1">Level 1 (Basic)</option>
              <option value="2">Level 2 (Intermediate)</option>
              <option value="3">Level 3 (Advanced)</option>
              <option value="4">Level 4 (Super)</option>
            </select>
          </div>
          <div className="form-group">
            <label className="toggle-label" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input type="checkbox" ref={isSystemRef} />
              <span>System Permission</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              const label = labelRef.current?.value?.trim() || '';
              const category = categoryRef.current?.value || '';
              const level = parseInt(levelRef.current?.value) || 1;

              if (!label) {
                showToastMessage('Please enter a permission label', 'error');
                return;
              }
              if (!category) {
                showToastMessage('Please select a category', 'error');
                return;
              }

              setIsSubmitting(true);
              try {
                const data = {
                  label,
                  category,
                  level,
                  description: descriptionRef.current?.value || '',
                  is_system: isSystemRef.current?.checked || false
                };

                const result = await dispatch(createPermission(data));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getPermissions({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getPermissionStats());
                  
                  toastShownRef.current = false;
                  showToastMessage('Permission created successfully!', 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to create permission';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Permission'}
          </button>
        </div>
      </div>
    );
  };

  const handleEditPermission = (permission) => {
    setEditingPermissionId(permission.id);
    setSelectedPermission(permission);
    
    toastShownRef.current = false;

    setTimeout(() => {
      if (labelRef.current) labelRef.current.value = permission.label || '';
      if (categoryRef.current) categoryRef.current.value = permission.category || '';
      if (descriptionRef.current) descriptionRef.current.value = permission.description || '';
      if (levelRef.current) levelRef.current.value = permission.level || 1;
      if (isSystemRef.current) isSystemRef.current.checked = permission.is_system || false;
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Permission: {permission.label}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Label <span style={{ color: 'var(--red)' }}>*</span></label>
            <input
              className="form-input"
              ref={labelRef}
              defaultValue={permission.label || ''}
            />
            <div className="form-hint">Slug will be auto-generated if label changes</div>
          </div>
          <div className="form-group">
            <label className="form-label">Category <span style={{ color: 'var(--red)' }}>*</span></label>
            <select className="form-select" ref={categoryRef} defaultValue={permission.category || ''}>
              {Array.isArray(categories) && categories.length > 0 ? (
                categories.map(catKey => (
                  <option key={catKey} value={catKey}>
                    {getCategoryDisplayName(catKey)}
                  </option>
                ))
              ) : (
                <option value="" disabled>No categories available</option>
              )}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              rows="2"
              ref={descriptionRef}
              defaultValue={permission.description || ''}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Level <span style={{ color: 'var(--red)' }}>*</span></label>
            <select className="form-select" ref={levelRef} defaultValue={permission.level || 1}>
              <option value="1">Level 1 (Basic)</option>
              <option value="2">Level 2 (Intermediate)</option>
              <option value="3">Level 3 (Advanced)</option>
              <option value="4">Level 4 (Super)</option>
            </select>
          </div>
          <div className="form-group">
            <label className="toggle-label" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input type="checkbox" ref={isSystemRef} defaultChecked={permission.is_system || false} />
              <span>System Permission</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              const label = labelRef.current?.value?.trim() || '';
              const category = categoryRef.current?.value || '';
              const level = parseInt(levelRef.current?.value) || 1;

              if (!label) {
                showToastMessage('Please enter a permission label', 'error');
                return;
              }
              if (!category) {
                showToastMessage('Please select a category', 'error');
                return;
              }

              setIsSubmitting(true);
              try {
                const data = {
                  label,
                  category,
                  level,
                  description: descriptionRef.current?.value || '',
                  is_system: isSystemRef.current?.checked || false
                };

                const result = await dispatch(updatePermission({ id: permission.id, data }));
                if (result.payload?.success) {
                  closeModal();
                  setEditingPermissionId(null);
                  setSelectedPermission(null);
                  await dispatch(getPermissions({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getPermissionStats());
                  
                  toastShownRef.current = false;
                  showToastMessage('Permission updated successfully!', 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to update permission';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    );
  };

  const handleDeletePermission = (permission) => {
    toastShownRef.current = false;

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Permission</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{permission.label}</strong>?
          </p>
          {permission.is_system && (
            <div style={{
              padding: '12px',
              background: 'var(--amber-bg)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--amber)',
              marginBottom: '16px'
            }}>
              <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This is a system permission. Deleting it may affect system functionality.
            </div>
          )}
          <div style={{
            padding: '12px',
            background: 'var(--red-bg)',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'var(--red)'
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This action cannot be undone. Users with this permission will lose access.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-danger btn-sm"
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deletePermission(permission.id));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getPermissions({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getPermissionStats());
                  
                  toastShownRef.current = false;
                  showToastMessage('Permission deleted successfully.', 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to delete permission';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Deleting...' : 'Delete Permission'}
          </button>
        </div>
      </div>
    );
  };

  const handleViewPermission = (permission) => {
    setSelectedPermission(permission);
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Permission Details</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Label</span>
              <span style={{ fontWeight: '600' }}>{permission.label}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Slug</span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--blue)' }}>
                {permission.slug}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Category</span>
              <span>
                <span className="badge badge-blue">{getCategoryDisplayName(permission.category)}</span>
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Level</span>
              <span>
                <span className={`badge ${permission.level === 4 ? 'badge-purple' : permission.level === 3 ? 'badge-navy' : permission.level === 2 ? 'badge-blue' : 'badge-amber'}`}>
                  Level {permission.level}
                </span>
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>System</span>
              <span>{permission.is_system ? <><CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> Yes</> : <><XCircle size={14} style={{ verticalAlign: '-2px' }} /> No</>}</span>
            </div>
            {permission.description && (
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Description</span>
                <span style={{ color: 'var(--text-mid)' }}>{permission.description}</span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Created</span>
              <span>{permission.created_at ? new Date(permission.created_at).toLocaleString() : 'N/A'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Updated</span>
              <span>{permission.updated_at ? new Date(permission.updated_at).toLocaleString() : 'N/A'}</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button className="btn btn-gold btn-sm" onClick={() => {
            closeModal();
            setTimeout(() => handleEditPermission(permission), 100);
          }}>
            <Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit
          </button>
        </div>
      </div>
    );
  };

  // ==================== USER PERMISSIONS ====================

  const handleGetUserPermissions = async () => {
    if (!selectedUserId) {
      showToastMessage('Please enter a User ID', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await dispatch(getUserPermissions(selectedUserId)).unwrap();
      if (result.success) {
        setUserPermissions(result.data?.permissions || []);
        setSelectedPermissions(result.data?.permission_ids || []);
        showToastMessage(`User permissions retrieved for user ${selectedUserId}`, 'success');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get user permissions';
      showToastMessage(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSyncUserPermissions = async () => {
    if (!selectedUserId) {
      showToastMessage('Please enter a User ID', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await dispatch(syncUserPermissions({
        userId: selectedUserId,
        permissions: selectedPermissions
      })).unwrap();
      
      if (result.success) {
        toastShownRef.current = false;
        showToastMessage(`User permissions synced for user ${selectedUserId}`, 'success');
        await handleGetUserPermissions();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to sync user permissions';
      showToastMessage(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== ROLE PERMISSIONS ====================

  const handleGetRolePermissions = async () => {
    if (!selectedRoleId) {
      showToastMessage('Please enter a Role ID', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await dispatch(getRolePermissions(selectedRoleId)).unwrap();
      if (result.success) {
        setRolePermissions(result.data?.permissions || []);
        setSelectedPermissions(result.data?.permission_ids || []);
        showToastMessage(`Role permissions retrieved for role ${selectedRoleId}`, 'success');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get role permissions';
      showToastMessage(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSyncRolePermissions = async () => {
    if (!selectedRoleId) {
      showToastMessage('Please enter a Role ID', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await dispatch(syncRolePermissions({
        roleId: selectedRoleId,
        permissions: selectedPermissions
      })).unwrap();
      
      if (result.success) {
        toastShownRef.current = false;
        showToastMessage(`Role permissions synced for role ${selectedRoleId}`, 'success');
        await handleGetRolePermissions();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to sync role permissions';
      showToastMessage(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== TOGGLE PERMISSION SELECTION ====================

  const togglePermissionSelection = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const toggleAllPermissions = (permissionList) => {
    const allIds = permissionList.map(p => p.id);
    const allSelected = allIds.every(id => selectedPermissions.includes(id));
    
    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderStats = () => {
    return (
      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold">
          <div className="stat-label">Total Permissions</div>
          <div className="stat-value">{stats.total || 0}</div>
          <div className="stat-icon"><Key size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">Categories</div>
          <div className="stat-value">{Array.isArray(categories) ? categories.length : 0}</div>
          <div className="stat-icon"><Folder size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card purple">
          <div className="stat-label">Level 4 (Super)</div>
          <div className="stat-value">{stats.by_level?.[4] || 0}</div>
          <div className="stat-icon"><Star size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">System Permissions</div>
          <div className="stat-value">{stats.system_count || 0}</div>
          <div className="stat-icon"><Settings size={28} strokeWidth={1.5} /></div>
        </div>
      </div>
    );
  };

  const renderTableView = () => {
    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Slug</th>
                <th>Category</th>
                <th>Level</th>
                <th>System</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {permissions.length === 0 ? (
                <tr>
                  <td colSpan="6">
                    <div className="empty-state">
                      <div className="empty-icon"><Key size={48} strokeWidth={1.5} /></div>
                      <div className="empty-title">No permissions found</div>
                      <div className="empty-text">Try adjusting your search or filters</div>
                    </div>
                  </td>
                </tr>
              ) : (
                permissions.map((permission) => (
                  <tr key={permission.id}>
                    <td>
                      <div style={{ fontWeight: '500', color: 'var(--navy)' }}>
                        {permission.label}
                      </div>
                    </td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', color: 'var(--blue)' }}>
                      {permission.slug}
                    </td>
                    <td>
                      <span className="badge badge-blue" style={{ fontSize: '10px' }}>
                        {getCategoryDisplayName(permission.category)}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${permission.level === 4 ? 'badge-purple' : permission.level === 3 ? 'badge-navy' : permission.level === 2 ? 'badge-blue' : 'badge-amber'}`}>
                        L{permission.level}
                      </span>
                    </td>
                    <td>
                      {permission.is_system ? (
                        <span style={{ color: 'var(--green)' }}><CheckCircle2 size={16} /></span>
                      ) : (
                        <span style={{ color: 'var(--text-light)' }}><XCircle size={16} /></span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleViewPermission(permission)}
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleEditPermission(permission)}
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleDeletePermission(permission)}
                          title="Delete"
                          style={{ color: 'var(--red)' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderGridView = () => {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
        {permissions.length === 0 ? (
          <div style={{ gridColumn: '1 / -1' }}>
            <div className="empty-state">
              <div className="empty-icon"><Key size={48} strokeWidth={1.5} /></div>
              <div className="empty-title">No permissions found</div>
              <div className="empty-text">Try adjusting your search or filters</div>
            </div>
          </div>
        ) : (
          permissions.map((permission) => (
            <div key={permission.id} className="card" style={{ borderTop: `3px solid ${permission.level === 4 ? '#7c3aed' : permission.level === 3 ? '#1a1a2e' : permission.level === 2 ? '#3b82f6' : '#f59e0b'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)' }}>
                    {permission.label}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)', fontFamily: 'JetBrains Mono, monospace' }}>
                    {permission.slug}
                  </div>
                </div>
                {permission.is_system && (
                  <span className="badge badge-purple" style={{ fontSize: '10px' }}>System</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                <span className="badge badge-blue" style={{ fontSize: '10px' }}>
                  {getCategoryDisplayName(permission.category)}
                </span>
                <span className={`badge ${permission.level === 4 ? 'badge-purple' : permission.level === 3 ? 'badge-navy' : permission.level === 2 ? 'badge-blue' : 'badge-amber'}`}>
                  Level {permission.level}
                </span>
              </div>
              {permission.description && (
                <div style={{ fontSize: '12px', color: 'var(--text-mid)', marginBottom: '10px' }}>
                  {permission.description}
                </div>
              )}
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '8px' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleViewPermission(permission)}
                >
                  <Eye size={16} style={{ verticalAlign: '-3px' }} /> View
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleEditPermission(permission)}
                >
                  <Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeletePermission(permission)}
                >
                  <Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const renderPermissionSync = () => {
    return (
      <div className="card">
        <div className="card-header">
          <div className="card-title"><Lock size={16} style={{ verticalAlign: '-3px' }} /> Permission Sync</div>
        </div>

        {/* User Permissions Section */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '12px', color: 'var(--navy)' }}><User size={16} style={{ verticalAlign: '-3px' }} /> User Permissions</h4>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <input
              type="text"
              className="form-input"
              style={{ width: '200px' }}
              placeholder="Enter User ID"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={isSubmitting}
            />
            <button 
              className="btn btn-ghost btn-sm" 
              onClick={handleGetUserPermissions}
              disabled={isSubmitting || !selectedUserId}
            >
              {isSubmitting ? 'Loading...' : 'Get Permissions'}
            </button>
            <button 
              className="btn btn-gold btn-sm" 
              onClick={handleSyncUserPermissions}
              disabled={isSubmitting || !selectedUserId || selectedPermissions.length === 0}
            >
              {isSubmitting ? 'Syncing...' : 'Sync Permissions'}
            </button>
          </div>
          
          {userPermissions.length > 0 && (
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', padding: '4px 0' }}>
                <strong style={{ fontSize: '13px' }}>Selected Permissions ({selectedPermissions.length})</strong>
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={() => toggleAllPermissions(userPermissions)}
                  disabled={isSubmitting}
                >
                  Toggle All
                </button>
              </div>
              {userPermissions.map(perm => (
                <div key={perm.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <input
                    type="checkbox"
                    checked={selectedPermissions.includes(perm.id)}
                    onChange={() => togglePermissionSelection(perm.id)}
                    disabled={isSubmitting}
                  />
                  <span style={{ fontSize: '13px' }}>{perm.label}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-light)', marginLeft: 'auto' }}>
                    {getCategoryDisplayName(perm.category)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Role Permissions Section */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '12px', color: 'var(--navy)' }}><Users size={16} style={{ verticalAlign: '-3px' }} /> Role Permissions</h4>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <input
              type="text"
              className="form-input"
              style={{ width: '200px' }}
              placeholder="Enter Role ID"
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              disabled={isSubmitting}
            />
            <button 
              className="btn btn-ghost btn-sm" 
              onClick={handleGetRolePermissions}
              disabled={isSubmitting || !selectedRoleId}
            >
              {isSubmitting ? 'Loading...' : 'Get Permissions'}
            </button>
            <button 
              className="btn btn-gold btn-sm" 
              onClick={handleSyncRolePermissions}
              disabled={isSubmitting || !selectedRoleId || selectedPermissions.length === 0}
            >
              {isSubmitting ? 'Syncing...' : 'Sync Permissions'}
            </button>
          </div>
          
          {rolePermissions.length > 0 && (
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', padding: '4px 0' }}>
                <strong style={{ fontSize: '13px' }}>Selected Permissions ({selectedPermissions.length})</strong>
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={() => toggleAllPermissions(rolePermissions)}
                  disabled={isSubmitting}
                >
                  Toggle All
                </button>
              </div>
              {rolePermissions.map(perm => (
                <div key={perm.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <input
                    type="checkbox"
                    checked={selectedPermissions.includes(perm.id)}
                    onChange={() => togglePermissionSelection(perm.id)}
                    disabled={isSubmitting}
                  />
                  <span style={{ fontSize: '13px' }}>{perm.label}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-light)', marginLeft: 'auto' }}>
                    {getCategoryDisplayName(perm.category)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    if (pagination.last_page <= 1) return null;

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
        marginTop: '20px',
        padding: '16px',
        background: 'var(--white)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow)'
      }}>
        <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
          Showing <strong>{((currentPage - 1) * itemsPerPage) + 1}</strong> to{' '}
          <strong>{Math.min(currentPage * itemsPerPage, pagination.total)}</strong> of{' '}
          <strong>{pagination.total}</strong> permissions
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
            <select
              className="form-select"
              style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              ← Prev
            </button>

            {getPageNumbers().map(page => (
              <button
                key={page}
                className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="btn btn-ghost btn-sm"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === pagination.last_page}
              style={{
                opacity: currentPage === pagination.last_page ? 0.5 : 1,
                cursor: currentPage === pagination.last_page ? 'not-allowed' : 'pointer'
              }}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <Key size={16} style={{ verticalAlign: '-3px' }} /> Manage permissions and access control for your organization.
        </div>
        <button
          className="btn btn-gold btn-sm"
          onClick={handleCreatePermission}
          disabled={isSubmitting}
        >
          + Create Permission
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
        <button
          className={`btn ${activeTab === 'permissions' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
          onClick={() => setActiveTab('permissions')}
        >
          <ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Permissions
        </button>
        <button
          className={`btn ${activeTab === 'sync' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
          onClick={() => setActiveTab('sync')}
        >
          <Lock size={16} style={{ verticalAlign: '-3px' }} /> Permission Sync
        </button>
      </div>

      {/* Stats - Only show on permissions tab */}
      {activeTab === 'permissions' && renderStats()}

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ marginBottom: '8px' }}><Hourglass size={20} /></div>
          <div style={{ color: 'var(--text-light)' }}>Loading permissions...</div>
        </div>
      ) : (
        <>
          {activeTab === 'permissions' ? (
            <>
              {/* Filters */}
              <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '12px',
                  alignItems: 'center'
                }}>
                  <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
                    <span className="search-icon"><Search size={16} /></span>
                    <input
                      type="text"
                      placeholder="Search permissions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <select
                    className="form-select"
                    style={{ width: '150px' }}
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Categories</option>
                    {Array.isArray(categories) && categories.map(catKey => (
                      <option key={catKey} value={catKey}>
                        {getCategoryDisplayName(catKey)}
                      </option>
                    ))}
                  </select>
                  <select
                    className="form-select"
                    style={{ width: '130px' }}
                    value={selectedLevel}
                    onChange={(e) => {
                      setSelectedLevel(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Levels</option>
                    <option value="1">Level 1</option>
                    <option value="2">Level 2</option>
                    <option value="3">Level 3</option>
                    <option value="4">Level 4</option>
                  </select>
                  <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                    <button
                      className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
                      onClick={() => setViewMode('table')}
                    >
                      <List size={16} style={{ verticalAlign: '-3px' }} /> List
                    </button>
                    <button
                      className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
                      onClick={() => setViewMode('grid')}
                    >
                      <LayoutGrid size={16} style={{ verticalAlign: '-3px' }} /> Grid
                    </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              {viewMode === 'table' ? renderTableView() : renderGridView()}
              {renderPagination()}
            </>
          ) : (
            renderPermissionSync()
          )}
        </>
      )}

      <style>{`
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-blue {
          background: var(--blue-bg);
          color: var(--blue);
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
        }
        .badge-purple {
          background: var(--purple-bg);
          color: var(--purple);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .stat-card {
          padding: 12px;
          border-radius: 10px;
          position: relative;
          overflow: hidden;
        }
        .stat-card .stat-label {
          font-size: 11px;
          color: var(--text-light);
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .stat-card .stat-value {
          font-size: 20px;
          font-weight: 700;
          font-family: Playfair Display, serif;
          color: var(--navy);
          margin-top: 2px;
        }
        .stat-card .stat-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 28px;
          opacity: 0.3;
        }
        .stat-card.gold { background: var(--gold-pale); }
        .stat-card.green { background: var(--green-bg); }
        .stat-card.blue { background: var(--blue-bg); }
        .stat-card.purple { background: var(--purple-bg); }
        .btn-primary {
          background: var(--gold);
          color: var(--navy);
          border: none;
        }
        .btn-primary:hover {
          background: var(--gold-dark);
        }
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-danger {
          background: var(--red);
          color: white;
          border: none;
        }
        .btn-danger:hover {
          background: var(--red-dark);
        }
        .btn-danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-gold:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-ghost:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .table-wrap {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          text-align: left;
          padding: 10px 12px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-light);
          border-bottom: 2px solid var(--border);
        }
        td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-light);
          font-size: 13px;
        }
        tr:hover td {
          background: var(--cream);
        }
        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: var(--text-light);
        }
        .empty-state .empty-icon {
          font-size: 48px;
          margin-bottom: 12px;
        }
        .empty-state .empty-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--navy);
          margin-bottom: 4px;
        }
        .empty-state .empty-text {
          font-size: 13px;
          color: var(--text-light);
        }
        .search-box {
          position: relative;
        }
        .search-box .search-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          opacity: 0.5;
        }
        .search-box input {
          padding-left: 32px;
          width: 100%;
        }
        .form-hint {
          font-size: 12px;
          color: var(--text-light);
          margin-top: 4px;
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
          }
        }
        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
      `}</style>
    </div>
  );
};

export default Permission;